<template>
  <div>
    <!-- Freigeschaltete Domains -->
    <DomainDropdown :server="showDomains" :email="showDomains"></DomainDropdown>
    

    <!-- Schlüsseltyp-Dropdown -->
    <b-dropdown
    id="keytypes"
    variant="primary"
    :text="t('allowed_keytypes')"
    >
      <b-dropdown-item-button v-for="keytype in props.keytypeOptions" :key="keytype.value">
        {{ keytype.text }}
      </b-dropdown-item-button>
    </b-dropdown>

    <br />

    <!-- Dateiupload -->
    <b-form-group
      :label="t('file_label')"
      :description="t('file_description')"
      :invalid-feedback="
        props.displayAllFeedback && fileState === null
          ? t('no_file_selected')
          : fileFeedback
      "
      :valid-feedback="fileFeedback"
      :state="props.displayAllFeedback && fileState === null ? false : fileState"
    >
      <b-form-file
        v-model="file"
        accept=".pem, .req, .csr, .p10"
        :state="props.displayAllFeedback && fileState === null ? false : fileState"
        :placeholder="t('file_placeholder')"
        :drop_placeholder="t('file_placeholder')"
        @input="uploadPKCS10"
      ></b-form-file>
    </b-form-group>

    <!-- DN-Präfixe -->
    <b-dropdown
      id="prefixes"
      variant="primary"
      :text="
        t(
          showOnlySTLPrefixesForServer
            ? 'available_dn_prefixes_stl'
            : 'available_dn_prefixes'
        )
      "
    >
      <b-dropdown-item-button v-for="prefix in custom.dnPrefixes" :key="prefix">
        {{ prefix }}
      </b-dropdown-item-button>
    </b-dropdown>
    <br />
  </div>
</template>

<i18n>
    # Die i18n-Strings sind in der Reihenfolge aufgeführt, in der sie der Benutzer zu sehen bekommt:
    # Strings, die noch nicht abgenommen wurden, sind mit einem Warndreieck markiert:
    en:
      available_dn_prefixes: "The subject DN in your CSR must end with one of the following strings:"
      available_dn_prefixes_stl: "The subject DN in your CSR must end with one of the following strings. For server certificates, only the variants with L and ST attributes may be used from this list:"
      allowed_keytypes: "Allowed Keytypes"
      file_label: "To upload your own CSR (PKCS#10) file you must have generated it locally, e.g. with openssl."
      file_description: "Your existing CSR (PKCS#10) file in PEM format. Commonly used file exentions are .pem and .csr."
      file_placeholder: "CSR (PKCS#10) file (e.g. .pem)"
      no_file_selected: "Please select your CSR file (PKCS#10) for uploading."
      parse_error: "The file you provided could not be parsed."
      syntax_error: "The value '{value}' is not allowed for item {type}."
      maxlength_exceeded: "The value '{value}' is too long for item {type}. Maximum length allowed for {type} is {maxLength} characters."
      no_cn: "The Subject Distinguished Name (subjectDN, sDN) in the CSR contains no Common Name (CN) attribute."
      no_cn_no_san: "The Subject Distinguished Name (subjectDN, sDN) in the CSR contains neither a Common Name (CN) attribute nor a Subject Alternative Name (SAN)."
      parsed_pem: "CSR for {CN}"
      upload: "upload CSR"
    de:
      available_dn_prefixes: "Der Subject-DN in Ihrem Zertifikatantrag muss auf einen der folgenden Namen enden:"
      available_dn_prefixes_stl: "Der Subject-DN in Ihrem Zertifikatantrag muss auf einen der folgenden Namen enden. Für Serverzertifikate dürfen aus dieser Liste nur die Varianten mit L- und ST-Attributen verwendet werden:"
      allowed_keytypes: "Erlaubte Schlüsseltypen"
      file_label: "Um einen CSR (PKCS10) einreichen zu können, müssen Sie diesen vorher erstellt haben, z.B. mit openssl."
      file_description: "Ihr vorhandener CSR (PKCS#10) im PEM-Format. Gebräuchliche Dateiendungen sind .pem und .csr."
      file_placeholder: "CSR-Datei (PKCS#10, z.B. .pem)"
      no_file_selected: "Bitte wählen Sie Ihre CSR-Datei (PKCS#10) zum Hochladen aus."
      parse_error: "Der CSR konnte nicht eingelesen werden."
      syntax_error: "Der Wert '{value}' ist im Eintrag {type} nicht erlaubt."
      maxlength_exceeded: "Der Wert '{value}' im Eintrag {type} ist zu lang. Es sind maximal {maxLength} Zeichen erlaubt."
      no_cn: "Der Zertifikatname (Subject Distinguished Name, subjectDN, sDN) im CSR enthält kein Common Name (CN) Attribut."
      no_cn_no_san: "Der Zertifikatname (Subject Distinguished Name, subjectDN, sDN) im CSR enthält weder ein Common Name (CN) Attribut, noch ein Subject Alternative Name (SAN)."
      parsed_pem: "CSR für {CN}"
      upload: "CSR hochladen"
</i18n>

<script setup>
import { defineEmits, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { custom } from "@/customization/customization";
import { Logger } from "@/logger/logger";
import { POLICY_PREFERENCES } from "@/customization/policy";
import { parsePKCS10 } from "@/pki/dfnpki";
import { extractPEM } from '@/utils/cryptoutils';
import { Subject } from "@/pki/subject";

import DomainDropdown from "@/components/pages/new-certificate/DomainDropdown";

const { t } = useI18n()

const logger = new Logger("Pkcs10Upload");

const emit = defineEmits(['input'])
const props = defineProps({
  certRole: { type: String, required: true },
  displayAllFeedback: { type: Boolean, required: true },
  value: { type: Object },
  keytypeOptions: { type: Array, required: true },
});

const file = ref(null);
const fileState = ref(null);
const fileFeedback = ref("");
const showDomains = custom.policy.newRequest.showDomains;
const showOnlySTLPrefixesForServer = custom.policy.newRequest.showOnlySTLPrefixesForServer;

const uploadPKCS10 = (uploadFile) => {
  if (!uploadFile) {
    throw new Error("keine PKCS#10-Datei ausgewählt.");
  }
  logger.info("Lese Datei " + uploadFile.name);

  const reader = new FileReader();

  reader.onload = (e) => parseFile(e.target.result);
  reader.readAsText(uploadFile);
};

/**
 * Zeigt eine Fehlermeldung.
 *
 * Setzt den fileState auf false und zeigt den in msg (optional mit params)
 * angegebenen i18n-String als Fehlermeldung in fileFeedback.
 * Außerdem wird null an die Elternkomponente emittet.
 */
const setError = (msg, params) => {
  fileState.value = false;
  fileFeedback.value = t(msg, params);
  emit("input", null);
};

/**
 * Prüft Syntax und Länge des Übergebenen Type-Value-Paares.
 *
 * Bei einem Fehler wird eine Fehlermeldung gesetzt und false zurückgegeben.
 */
const checkName = (type, value) => {
  if (!type.pattern.test(value)) {
    setError("syntax_error", { type: type.shortName, value: value });
    return false;
  }
  if (value.length > type.maxLength) {
    setError("maxlength_exceeded", {
      type: type.shortName,
      value: value,
      maxLength: type.maxLength,
    });
    return false;
  }
  logger.trace(type.shortName + " mit Wert " + value + " ok.");
  return true;
};

watch(() => props.certRole, 
  (newCertRole, oldCertRole) => {
    logger.debug(`certrole changed to ${newCertRole}`);
    uploadPKCS10(file.value);
});

const parseFile = (content) => {
  var pem;
  var requestData;
  try {
    pem = extractPEM("Inhalt der PKCS10-Datei", content);
    requestData = parsePKCS10(pem);
  } catch (e) {
    logger.error("Konnte Datei nicht parsen: " + e);
    setError("parse_error");
    return;
  }
  // Antrag erfolgreich eingelesen --> Syntaxprüfung für DN und Altnames
  for (let name of requestData.rdns.concat(requestData.altnames)) {
    const nameOk = checkName(name.type, name.value);
    if (!nameOk) {
      return;
    }
  }
  // Syntaxprüfung bestanden
  const subject = Subject.fromTypeValueArray(requestData.rdns);
  const altnames = requestData.altnames;
  logger.debug("Antrag eingelesen. DN: " + subject);

  fileState.value = true;

  if ( custom.serverRoles.includes(props.certRole) &&
  (custom.policy == POLICY_PREFERENCES.Community || 
  custom.policy == POLICY_PREFERENCES.Kommerz || 
  custom.policy == POLICY_PREFERENCES.Internal) ) {

    if (!subject.hasCN()) {

      if (altnames.length != 0) { // Server Cert ohne CN aber mit SAN
        fileFeedback.value = t("parsed_pem", { CN: altnames[0].value });
      } else { //Error kein CN & kein SAN
        setError("no_cn_no_san");
        return;
      }
    } else { //CN existiert, SANs egal (werden in Ocaso hinzugefügt)
      fileFeedback.value = t("parsed_pem", { CN: subject.getCN() });
    }
    
  } else {
    try {
      fileFeedback.value = t("parsed_pem", { CN: subject.getCN() });
    } catch (e) {
      setError("no_cn");
      return;
    }
  }

  // PEM, Subject und Altnames an NewRequest.vue übergeben.
  emit("input", { pem: pem, subject: subject, altnames: altnames });
};
</script>

<style scoped>
#prefixes {
  margin-top: 10px;
  margin-bottom: 10px;
}
#keytypes {
  margin-top: 10px;
  margin-bottom: 10px;
}
</style>
